import type { Photo } from './AevocamViewer'

/** Stable identity for a photo across manifest refetches / hour filters. */
export type PhotoAnchor = Pick<Photo, 'd' | 't' | 's'>

/** Photos to restore after toggling the hour filter. */
export type FilterChangeAnchors = {
  current: PhotoAnchor | null
  in: PhotoAnchor | null
  out: PhotoAnchor | null
}

//--------------------------------------------------------------------------------
export function photoToAnchor(photo: Photo | undefined): PhotoAnchor | undefined {
  if (!photo) return undefined
  return { d: photo.d, t: photo.t, s: photo.s }
}

//--------------------------------------------------------------------------------
export function photosMatch(photo: Photo, anchor: PhotoAnchor): boolean {
  return photo.d === anchor.d && photo.t === anchor.t && photo.s === anchor.s
}

//--------------------------------------------------------------------------------
function dateToMs(d: string): number {
  const parts = d.split('-').map(Number)
  if (parts.length !== 3 || parts.some(n => Number.isNaN(n))) return 0
  const [y, m, day] = parts
  return Date.UTC(y!, m! - 1, day!)
}

//--------------------------------------------------------------------------------
/**
 * Pick an index in a new photo list after toggling or changing the hour filter.
 * Prefers the exact photo, then same calendar day, then nearest day.
 */
export function findPhotoIndexAfterTimeSlotChange(
  photos: readonly (Photo | undefined)[],
  anchor: PhotoAnchor,
): number {
  if (photos.length === 0) return 0

  const exact = photos.findIndex(p => p !== undefined && photosMatch(p, anchor))
  if (exact >= 0) return exact

  const sameDay: number[] = []
  for (let i = 0; i < photos.length; i++) {
    const p = photos[i]
    if (p && p.d === anchor.d) sameDay.push(i)
  }

  if (sameDay.length === 1) return sameDay[0]!

  if (sameDay.length > 1) {
    let best = sameDay[0]!
    let bestDelta = Infinity
    for (const i of sameDay) {
      const p = photos[i]!
      const delta = Math.abs(p.t - anchor.t)
      if (delta < bestDelta) {
        bestDelta = delta
        best = i
      }
    }
    return best
  }

  const anchorMs = dateToMs(anchor.d)
  let bestIdx = 0
  let bestDelta = Infinity
  for (let i = 0; i < photos.length; i++) {
    const p = photos[i]
    if (!p) continue
    const delta = Math.abs(dateToMs(p.d) - anchorMs)
    if (delta < bestDelta) {
      bestDelta = delta
      bestIdx = i
    }
  }
  return bestIdx
}
